import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Loader2, Send, Sparkles, Trash2 } from 'lucide-react';
import { aiAPI } from '../../services/api';
import toast from '../../services/toast';
import { VANTI_NOT_CONFIGURED_MESSAGE } from '../../utils/aiSetupHelp';

function chipsFromSources(sources) {
  const list = Array.isArray(sources) ? sources : [];
  return list.filter((s) => typeof s === 'string' && s.trim());
}

export default function ProjectAskPanel({ projectId, streamId }: any) {
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [configured, setConfigured] = useState<any>(null);
  const [cooldownUntil, setCooldownUntil] = useState<any>(null);
  const [lastSendBody, setLastSendBody] = useState<any>(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const listRef = useRef<any>(null);

  const suggestions = useMemo(
    () => ['What does this project do?', 'How do we deploy?', 'Where is auth handled?'],
    []
  );

  useEffect(() => {
    aiAPI
      .status()
      .then((r) => setConfigured(r.configured ?? false))
      .catch(() => setConfigured(false));
  }, []);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const cooldownRemainingMs = cooldownUntil ? Math.max(0, cooldownUntil - Date.now()) : 0;
  const onCooldown = cooldownRemainingMs > 0;

  useEffect(() => {
    if (!onCooldown) return;
    const t = setInterval(() => setCooldownUntil((v) => v), 250);
    return () => clearInterval(t);
  }, [onCooldown]);

  const send = async (text?: any) => {
    const q = String(text ?? input).trim();
    if (!q || loading || onCooldown) return;
    if (configured === false) {
      toast.error(VANTI_NOT_CONFIGURED_MESSAGE);
      return;
    }

    setInput('');
    const userMsg = { role: 'user', content: q };
    const next = [...messages, userMsg];
    setMessages(next);
    setLoading(true);

    try {
      const navigationContext: any = { projectId };
      if (streamId) navigationContext.streamId = streamId;
      const body = {
        mode: 'projectAsk',
        messages: next.map((m) => ({ role: m.role, content: m.content })),
        navigationContext,
      };
      setLastSendBody(body);
      const res = await aiAPI.chat(body);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: res.reply,
          detail: res.detail || '',
          sources: chipsFromSources(res.sources),
        },
      ]);
    } catch (e: any) {
      if (
        e &&
        (e.code === 'AI_RATE_LIMIT' || e.code === 'AI_DUPLICATE' || e.code === 'AI_TIMEOUT')
      ) {
        const retryAfterMs =
          Number(e?.meta?.retryAfterMs) || (e.code === 'AI_DUPLICATE' ? 1000 : 5000);
        setCooldownUntil(Date.now() + Math.max(500, retryAfterMs));
      }
      toast.error((e as Error).message || 'Failed to get AI response');
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const retryLast = async () => {
    if (!lastSendBody || loading || onCooldown) return;
    setLoading(true);
    try {
      const res = await aiAPI.chat(lastSendBody);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: res.reply,
          detail: res.detail || '',
          sources: chipsFromSources(res.sources),
        },
      ]);
    } catch (e: any) {
      if (
        e &&
        (e.code === 'AI_RATE_LIMIT' || e.code === 'AI_DUPLICATE' || e.code === 'AI_TIMEOUT')
      ) {
        const retryAfterMs =
          Number(e?.meta?.retryAfterMs) || (e.code === 'AI_DUPLICATE' ? 1000 : 5000);
        setCooldownUntil(Date.now() + Math.max(500, retryAfterMs));
      }
      toast.error((e as Error).message || 'Failed to get AI response');
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    setMessages([]);
    setExpanded(new Set());
  };

  const toggleExpanded = (idx) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <div className="card-mdl-static flex flex-col overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-dark-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-vivid-purple" aria-hidden />
          <div>
            <div className="text-sm font-semibold text-slate-100">Ask this project</div>
            <div className="text-xs text-slate-400">Cited answers from code + documents</div>
          </div>
        </div>
        {messages.length ? (
          <button
            type="button"
            className="btn-icon-mdl text-xs"
            onClick={clear}
            title="New chat"
            aria-label="New chat"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
      </div>

      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
        {onCooldown ? (
          <div className="rounded-lg border-amber-900/40 bg-amber-900/20 px-3 py-2 text-amber-200">
            Rate limited. You can retry in {Math.ceil(cooldownRemainingMs / 1000)}s.
            {lastSendBody ? (
              <button
                type="button"
                className="ml-2 underline"
                onClick={retryLast}
                disabled={onCooldown || loading}
              >
                Retry
              </button>
            ) : null}
          </div>
        ) : null}
        {messages.length === 0 && !loading ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-400">Try one of these to start:</p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="rounded-full border border-dark-border bg-dark-base/30/50 px-3 py-1 text-xs text-slate-300 hover:bg-dark-base"
                  onClick={() => send(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {messages.map((m, idx) => (
          <div key={idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[90%] rounded-2xl px-3 py-2 text-sm ${
                m.role === 'user' ? 'bg-vivid-purple text-white' : 'bg-dark-elevated text-slate-100'
              }`}
            >
              <div className="whitespace-pre-wrap break-words">{m.content}</div>

              {m.role === 'assistant' ? (
                <>
                  {m.detail ? (
                    <button
                      type="button"
                      className="mt-2 text-xs underline text-slate-300"
                      onClick={() => toggleExpanded(idx)}
                    >
                      {expanded.has(idx) ? 'Hide details' : 'Show details'}
                    </button>
                  ) : null}

                  {m.detail && expanded.has(idx) ? (
                    <div className="mt-2 whitespace-pre-wrap break-words text-sm text-slate-200">
                      {m.detail}
                    </div>
                  ) : null}

                  {Array.isArray(m.sources) && m.sources.length ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {m.sources.map((s) => (
                        <span
                          key={s}
                          className="rounded bg-dark-elevated px-2 py-0.5 text-[11px] font-mono text-slate-300"
                          title={s}
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        ))}

        {loading ? (
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            <span>Thinking…</span>
          </div>
        ) : null}
      </div>

      <div className="border-t border-dark-border p-3">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask a question…"
            className="flex-1 rounded-xl border border-dark-border bg-dark-base px-3 py-2 text-sm text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-vivid-purple"
            disabled={loading || onCooldown}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                send();
              }
            }}
          />
          <button
            type="button"
            onClick={() => send()}
            disabled={loading || onCooldown || !input.trim()}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-vivid-purple text-white disabled:opacity-50"
            aria-label="Send"
          >
            <Send className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}
